import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';

import AutoFillPersonalDetails from './AutoFillPersonalDetails';
import {
  EMAIL_VALIDATION_ERROR_ID,
  PHONE_VALIDATION_ERROR_ID_PREFIX,
  WCAG_INLINE_ERROR_CLASS
} from './constants';
import queryClient from './queryClient';
import { SectionHeading } from './SectionHeading';
import { classes } from './styles';
import { GooglePlacesAutocomplete } from '../NewUI/Components/CustomUIElements/GooglePlacesAutocomplete';
import { parseCountryData } from '../NewUI/Components/Utilities/countryParser';
import { parseGooglePlacePrediction } from '../NewUI/Components/Utilities/googlePlaceAddress';

import type { AutoFillConfig, AutoFillResumeFile, CandidateDetailsText } from './types';

interface CandidateDetailsProps {
  formHtml?: string;
  personalInformationLabel?: string;
  fontFamily?: string;
  autoFillConfig?: AutoFillConfig;
  candidateDetailsText: CandidateDetailsText;
  isParsing?: boolean;
  parseError?: string;
  onParsingStateChange?: (parsing: boolean, error?: string) => void;
  selectedResumeFile?: AutoFillResumeFile;
  onSelectedResumeFileChange?: (file: AutoFillResumeFile) => void;
}

// qlty-disable-next-line return-statements
const CandidateDetailsContent: React.FC<CandidateDetailsProps> = ({
  formHtml,
  personalInformationLabel = 'Personal Information',
  fontFamily,
  autoFillConfig,
  candidateDetailsText,
  isParsing = false,
  parseError = '',
  onParsingStateChange,
  selectedResumeFile,
  onSelectedResumeFileChange
}) => {
  const [parseProgress, setParseProgress] = useState(0);
  const [showParseProgress, setShowParseProgress] = useState(false);
  const [addressLookupContainer, setAddressLookupContainer] = useState<HTMLElement | null>(null);
  const [addressLookupValue, setAddressLookupValue] = useState('');
  const formInnerHtml = useMemo(() => ({ __html: formHtml ?? '' }), [formHtml]);
  // UIUX-2968: isError + refetch surface fetch failures (country/state is required, so users need feedback).
  const {
    data: countries,
    isSuccess: countriesReady,
    isError: countriesError,
    refetch: refetchCountries
  } = useQuery({
    queryKey: ['countries states'],
    queryFn: async () => {
      const response = await fetch('/api/system/v1/countries_states.json');
      if (!response.ok) {
        throw new Error('Failed to fetch countries and states');
      }
      return response.json();
    },
    enabled: !!formHtml
  });

  const addressLookupEnabled = Boolean(formHtml?.includes('candidate-address-lookup-container'));

  useEffect(() => {
    if (!addressLookupEnabled) return undefined;

    const timer = window.setTimeout(() => {
      setAddressLookupContainer(document.getElementById('candidate-address-lookup-container'));
      const sourceInput = document.getElementById(
        'application_candidate_attributes_address_attributes_street1'
      ) as HTMLInputElement | null;
      setAddressLookupValue(sourceInput?.value ?? '');
    }, 0);

    return () => window.clearTimeout(timer);
  }, [addressLookupEnabled, formHtml]);

  const handleAddressLookup = useCallback(
    async (prediction: google.maps.places.AutocompletePrediction) => {
      const parsedAddress = await parseGooglePlacePrediction(
        prediction,
        parseCountryData(countries ?? [])
      );
      if (!parsedAddress) return;

      const setValue = (id: string, value: string) => {
        if (!value) return;
        const input = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
        if (!input) return;
        if (input.value === value) return;
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      setAddressLookupValue(parsedAddress.description);
      setValue('application_candidate_attributes_address_attributes_street1', parsedAddress.street);
      setValue('application_candidate_attributes_address_attributes_suburb', parsedAddress.city);
      setValue(
        'application_candidate_attributes_address_attributes_postcode',
        parsedAddress.postcode
      );

      if (parsedAddress.matchedCountry) {
        setValue(
          'application_candidate_attributes_address_attributes_country_id',
          String(parsedAddress.matchedCountry.id)
        );

        const matchedState = parsedAddress.matchedCountry.states.find(
          (state) => state.name.toUpperCase() === parsedAddress.state.toUpperCase()
        );
        if (matchedState) {
          window.setTimeout(
            () =>
              setValue(
                'application_candidate_attributes_address_attributes_state_id',
                String(matchedState.id)
              ),
            0
          );
        }
      }
    },
    [countries]
  );

  useEffect(() => {
    if (!formHtml || !countries) {
      return undefined;
    }

    const windowWithCandidateLink = window as typeof window & {
      CandidateLink?: {
        countries?: {
          id: number;
          name: string;
          states: { id: number; name: string }[];
        }[];
      };
    };

    windowWithCandidateLink.CandidateLink ??= {};
    windowWithCandidateLink.CandidateLink.countries = countries;

    const selectCountry = document.getElementById(
      'application_candidate_attributes_address_attributes_country_id'
    ) as HTMLSelectElement | null;
    const selectState = document.getElementById(
      'application_candidate_attributes_address_attributes_state_id'
    ) as HTMLSelectElement | null;

    if (!selectCountry || !selectState) {
      return undefined;
    }

    const statePlaceholder =
      selectState.dataset.placeholder?.trim() ?? selectState.options[0]?.textContent?.trim() ?? '';

    const resetStateOptions = () => {
      selectState.replaceChildren(new Option(statePlaceholder, ''));
    };

    let previousCountryValue = selectCountry.value;

    const reloadStates = function reloadStates() {
      const countryValue = selectCountry.value;
      const previousStateValue = countryValue === previousCountryValue ? selectState.value : '';
      const val = parseInt(selectCountry.value, 10);
      if (Number.isNaN(val)) {
        resetStateOptions();
        previousCountryValue = countryValue;
        return;
      }

      const country = countries.find((c: { id: number }) => c.id === val);

      resetStateOptions();

      if (!country?.states) {
        previousCountryValue = countryValue;
        return;
      }

      let otherState: { name: string; id: number } | undefined;
      country.states.forEach((state: { name: string; id: number }) => {
        if (state.name === 'Other') {
          otherState = state;
          return;
        }
        const option = document.createElement('option');
        option.value = String(state.id);
        option.textContent = state.name;
        selectState.appendChild(option);
      });

      if (otherState) {
        const option = document.createElement('option');
        option.value = String(otherState.id);
        option.textContent = otherState.name;
        selectState.appendChild(option);
      }

      if (
        previousStateValue &&
        Array.from(selectState.options).some((option) => option.value === previousStateValue)
      ) {
        selectState.value = previousStateValue;
      }
      previousCountryValue = countryValue;
    };

    selectCountry.addEventListener('change', reloadStates);
    reloadStates();

    const initialStateId = selectState.getAttribute('data-initial-id');
    if (initialStateId) {
      selectState.value = initialStateId;
    }

    return function cleanupEffect() {
      selectCountry.removeEventListener('change', reloadStates);
    };
  }, [formHtml, countries]);

  useEffect(() => {
    if (!formHtml) {
      return undefined;
    }

    const phoneCleanups: (() => void)[] = [];
    let emailCleanup: (() => void) | null = null;

    // Returns true when ApplicationFlow's submit pipeline already owns the
    // error display for this input (aria-invalid + aria-describedby pointing
    // to an element we didn't create). Prevents duplicate error messages.
    const isPipelineOwnedError = (target: HTMLInputElement, localErrorId: string): boolean => {
      const describedById = target.getAttribute('aria-describedby');
      return (
        target.getAttribute('aria-invalid') === 'true' &&
        !!describedById &&
        describedById !== localErrorId &&
        !!document.getElementById(describedById)
      );
    };

    const ensureInlineErrorElement = (input: HTMLInputElement, errorId: string): HTMLElement => {
      let errorElement = document.getElementById(errorId);
      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.id = errorId;
        errorElement.classList.add(WCAG_INLINE_ERROR_CLASS);
        errorElement.setAttribute('role', 'alert');
        errorElement.setAttribute('data-validation-error', '');
        errorElement.style.cssText = classes.candidateDetails.phoneErrorCssText;
        input.parentNode?.insertBefore(errorElement, input.nextSibling);
      }
      return errorElement;
    };

    const showInlineInputError = (
      input: HTMLInputElement,
      errorId: string,
      message: string
    ): void => {
      const errorElement = ensureInlineErrorElement(input, errorId);
      errorElement.textContent = message;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorId);
      input.style.setProperty(
        'border-color',
        classes.candidateDetails.phoneErrorBorderColor,
        'important'
      );
    };

    const clearInlineInputError = (input: HTMLInputElement, errorId: string): void => {
      const errorElement = document.getElementById(errorId);
      if (errorElement) {
        errorElement.remove();
      }

      if (input.getAttribute('aria-describedby') !== errorId) {
        return;
      }

      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
      input.style.removeProperty('border-color');
    };

    const validatePhone = (value: string) => {
      const phoneRGEX = /^[+-\s0-9()]*$/;
      return phoneRGEX.test(value);
    };

    const buildPhoneValidationErrorId = (inputId: string): string =>
      `${PHONE_VALIDATION_ERROR_ID_PREFIX}-${inputId}`;

    const initializePhoneValidation = function initializePhoneValidation(inputId: string) {
      const phoneInput = document.getElementById(inputId) as HTMLInputElement | null;
      if (!phoneInput) {
        return;
      }

      const invalidPhoneMessage = phoneInput.dataset.invalidPhoneMessage?.trim();
      if (!invalidPhoneMessage) {
        return;
      }

      const errorId = buildPhoneValidationErrorId(phoneInput.id);

      const createOrUpdateErrorMessage = (show: boolean) => {
        if (show) {
          showInlineInputError(phoneInput, errorId, invalidPhoneMessage);
        } else {
          clearInlineInputError(phoneInput, errorId);
        }
      };

      const handleInput = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const isValid = validatePhone(target.value);
        if (!isValid) {
          target.setCustomValidity(invalidPhoneMessage);
          createOrUpdateErrorMessage(true);
        } else {
          target.setCustomValidity('');
          createOrUpdateErrorMessage(false);
        }
      };

      const handleBlur = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!validatePhone(target.value)) {
          if (isPipelineOwnedError(target, errorId)) return;
          createOrUpdateErrorMessage(true);
        }
      };

      phoneInput.addEventListener('input', handleInput);
      phoneInput.addEventListener('blur', handleBlur);

      phoneCleanups.push(() => {
        phoneInput.removeEventListener('input', handleInput);
        phoneInput.removeEventListener('blur', handleBlur);
        clearInlineInputError(phoneInput, errorId);
      });
    };

    const initializeEmailValidation = function initializeEmailValidation() {
      const emailInput = document.getElementById(
        'application_candidate_attributes_email'
      ) as HTMLInputElement | null;

      if (!emailInput) {
        return;
      }

      const validateEmail = (value: string) => {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
          return true;
        }

        const emailRGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return trimmed.length >= 5 && emailRGEX.test(trimmed);
      };

      const getEmailErrorMessage = (value: string) => {
        if (value.trim().length < 5) {
          return 'E-mail is too short (minimum is 5 characters)';
        }
        return 'Please enter a valid e-mail address';
      };

      const createOrUpdateErrorMessage = (show: boolean, message = '') => {
        const errorId = EMAIL_VALIDATION_ERROR_ID;
        if (show) {
          showInlineInputError(emailInput, errorId, message);
        } else {
          clearInlineInputError(emailInput, errorId);
        }
      };

      const handleInput = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const { value } = target;
        const trimmed = value.trim();

        if (trimmed.length === 0) {
          target.setCustomValidity('');
          createOrUpdateErrorMessage(false);
          return;
        }

        if (!validateEmail(value)) {
          const message = getEmailErrorMessage(value);
          target.setCustomValidity(message);
          createOrUpdateErrorMessage(true, message);
        } else {
          target.setCustomValidity('');
          createOrUpdateErrorMessage(false);
        }
      };

      const handleBlur = (e: Event) => {
        const target = e.target as HTMLInputElement;
        const { value } = target;
        if (value.trim().length === 0) {
          return;
        }
        if (!validateEmail(value)) {
          if (isPipelineOwnedError(target, EMAIL_VALIDATION_ERROR_ID)) return;
          const message = getEmailErrorMessage(value);
          target.setCustomValidity(message);
          createOrUpdateErrorMessage(true, message);
        }
      };

      emailInput.addEventListener('input', handleInput);
      emailInput.addEventListener('blur', handleBlur);

      emailCleanup = function emailCleanupFunction() {
        emailInput.removeEventListener('input', handleInput);
        emailInput.removeEventListener('blur', handleBlur);
        clearInlineInputError(emailInput, EMAIL_VALIDATION_ERROR_ID);
      };
    };

    initializePhoneValidation('application_candidate_attributes_phone1');
    initializePhoneValidation('application_candidate_attributes_phone2');
    initializeEmailValidation();

    return function cleanupEffect() {
      phoneCleanups.forEach((cleanup) => cleanup());
      if (emailCleanup) {
        emailCleanup();
      }
    };
  }, [formHtml]);

  useEffect(() => {
    const section = document.getElementById('candidate-details');
    if (!section) {
      return undefined;
    }

    section.toggleAttribute('inert', isParsing);
    section.setAttribute('aria-busy', String(isParsing));
    section.style.cursor = isParsing ? 'default' : '';

    return () => {
      section.removeAttribute('inert');
      section.removeAttribute('aria-busy');
      section.style.cursor = '';
    };
  }, [isParsing]);

  const handleParsingProgressChange = useCallback((progress: number, visible: boolean) => {
    setParseProgress(progress);
    setShowParseProgress(visible);
  }, []);

  if (!formHtml) {
    return null;
  }

  return (
    <Box
      aria-labelledby="candidate-details-heading"
      component="section"
      id="candidate-details"
      sx={classes.candidateDetails.containerSx()}
    >
      <Box sx={classes.candidateDetails.contentWrapperSx()}>
        <Box sx={classes.candidateDetails.headingRowSx()}>
          <SectionHeading fontFamily={fontFamily} id="candidate-details-heading" variant="inline">
            {personalInformationLabel}
          </SectionHeading>
          {showParseProgress ? (
            <Box sx={classes.candidateDetails.parsingIndicatorSx()}>
              <Box sx={classes.candidateDetails.parsingProgressWrapperSx()}>
                <CircularProgress
                  size={32}
                  sx={classes.candidateDetails.parsingProgressCircleSx()}
                  value={parseProgress}
                  variant="determinate"
                />
                <Typography component="span" sx={classes.candidateDetails.parsingProgressLabelSx()}>
                  {parseProgress}%
                </Typography>
              </Box>
              <Typography component="span" sx={classes.candidateDetails.parsingStatusTextSx()}>
                {candidateDetailsText.parsingStatus}
              </Typography>
            </Box>
          ) : null}
        </Box>
        {/* UIUX-2968: Error state for countries/states fetch failure.
            Shows an accessible alert with retry button when the API call fails.
            role="alert" carries an implicit aria-live="assertive" per ARIA spec, no
            explicit aria-live needed (adding aria-live="polite" would override it). */}
        {countriesError ? (
          <Box role="alert" sx={classes.candidateDetails.countriesErrorAlertSx()}>
            <Typography sx={classes.candidateDetails.countriesErrorMessageSx()}>
              Unable to load country and state options. Please try again.
            </Typography>
            <Box
              component="button"
              onClick={async () => refetchCountries()}
              sx={classes.candidateDetails.countriesErrorRetryButtonSx()}
              type="button"
            >
              Retry
            </Box>
          </Box>
        ) : null}
        <AutoFillPersonalDetails
          config={autoFillConfig}
          countriesReady={countriesReady}
          fontFamily={fontFamily}
          isParsing={isParsing}
          onParsingProgressChange={handleParsingProgressChange}
          onParsingStateChange={onParsingStateChange}
          onSelectedResumeFileChange={onSelectedResumeFileChange}
          parseError={parseError}
          selectedResumeFile={selectedResumeFile}
        />
        <Box
          dangerouslySetInnerHTML={formInnerHtml}
          sx={classes.candidateDetails.formFieldsSx(fontFamily)}
        />
        {addressLookupContainer
          ? createPortal(
              <GooglePlacesAutocomplete
                inputId="candidate-address-lookup"
                onPlaceSelect={handleAddressLookup}
                placeholder="Type an address to auto-fill the fields below..."
                value={addressLookupValue}
                onValueChange={(value) => {
                  setAddressLookupValue(value);
                  const sourceInput = document.getElementById(
                    'application_candidate_attributes_address_attributes_street1'
                  ) as HTMLInputElement | null;
                  if (!sourceInput) return;
                  sourceInput.value = value;
                  sourceInput.dispatchEvent(new Event('input', { bubbles: true }));
                }}
              />,
              addressLookupContainer
            )
          : null}
      </Box>
    </Box>
  );
};

export const CandidateDetails: React.FC<CandidateDetailsProps> = (props) => (
  <QueryClientProvider client={queryClient}>
    <CandidateDetailsContent {...props} />
  </QueryClientProvider>
);

export default CandidateDetails;
